/**
 * Appends the next page of blog cards in place of a full navigation.
 *
 * The button is a real link to the next page, so pagination still works with
 * JavaScript disabled; this element only upgrades it.
 */
class TdBlogLoadMore extends HTMLElement {
  /** @type {HTMLAnchorElement | null} */
  button = null;

  /** @type {HTMLElement | null} */
  grid = null;

  /** @type {boolean} */
  loading = false;

  /** @type {HTMLElement | null} */
  status = null;

  connectedCallback() {
    this.button = /** @type {HTMLAnchorElement | null} */ (
      this.querySelector('[data-td-load-more-button]')
    );

    const { gridId } = this.dataset;
    this.grid = gridId ? document.getElementById(gridId) : null;

    if (!this.button || !this.grid) return;

    this.status = document.createElement('span');
    this.status.className = 'visually-hidden';
    this.status.setAttribute('role', 'status');
    this.append(this.status);

    this.button.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
  }

  /**
   * @param {MouseEvent} event
   */
  onClick = async (event) => {
    const { button, grid } = this;
    const url = button?.getAttribute('href');
    const { sectionId } = this.dataset;

    if (!button || !grid || !url || !sectionId || this.loading) return;

    event.preventDefault();
    this.loading = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const target = new URL(url, window.location.origin);
      target.searchParams.set('section_id', sectionId);

      const response = await fetch(target.toString());
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);

      const markup = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextGrid = markup.querySelector('[data-td-blog-grid]');
      const nextButton = markup.querySelector('[data-td-load-more-button]');
      const nextHref = nextButton?.getAttribute('href');

      if (nextGrid) {
        const appended = [...nextGrid.children];
        grid.append(...appended);

        // The two grids differ: one wraps each card, the other appends anchors directly.
        const first = appended[0];
        const target = first?.matches('a') ? first : first?.querySelector('a');
        target?.focus({ preventScroll: true });

        if (this.status) {
          const template = this.dataset.loadedMessage || '[count] more results loaded';
          this.status.textContent = template.replace('[count]', String(appended.length));
        }
      }

      if (nextHref) {
        button.setAttribute('href', nextHref);
      } else {
        // Remove only the button: the live region must survive to be announced.
        button.remove();
        this.button = null;
        this.toggleAttribute('data-td-exhausted', true);
        return;
      }
    } catch (error) {
      // Leave the button as a working link so the next click navigates instead.
      console.error(error);
      button.removeEventListener('click', this.onClick);
    } finally {
      this.loading = false;
      button.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('td-blog-load-more')) {
  customElements.define('td-blog-load-more', TdBlogLoadMore);
}
